/**
 * Cooking prep progress bar (#5 cooking client wiring).
 *
 * Campfire (cooking) recipes don't craft instantly — the server registers a
 * deferred prep timer (server/items/craft-prep.ts) and broadcasts:
 *   - `craft-prep`  { sessionId, recipeId, totalMs }  on start
 *   - `craft-done`  { sessionId, recipeId, success }   on completion
 * main.ts filters those to the local player and drives this widget.
 *
 * The fill animates client-side via a CSS width transition over `totalMs`
 * (cook-speed is already baked into totalMs server-side). A safety timeout
 * auto-hides the bar a couple seconds past the expected finish, because the
 * server drops a prep WITHOUT a craft-done broadcast if the cube dies mid-cook
 * (craft-prep.ts tickCraftPrep) — without the fallback the bar would stick.
 */
import { ITEMS, RECIPES } from "@tribez/shared";

export interface CookProgress {
  /** Begin a cook; animate the fill to 100% over totalMs. */
  start(recipeId: string, totalMs: number): void;
  /** Cook finished (or failed) — hide the bar and toast the result. */
  finish(recipeId: string, success: boolean): void;
}

function recipeLabel(recipeId: string): string {
  const r = RECIPES[recipeId];
  const item = r ? ITEMS[r.outputItemId] : undefined;
  return item?.displayName ?? recipeId;
}

export function createCookProgress(notify?: (msg: string) => void): CookProgress {
  const root = document.createElement("div");
  root.id = "cook-progress";
  root.style.display = "none";
  root.innerHTML = `
    <div class="cook-progress__label"></div>
    <div class="cook-progress__track"><div class="cook-progress__fill"></div></div>
  `;
  document.body.appendChild(root);
  const labelEl = root.querySelector<HTMLDivElement>(".cook-progress__label")!;
  const fillEl = root.querySelector<HTMLDivElement>(".cook-progress__fill")!;

  let safetyTimer: number | null = null;
  // Which cook the bar is currently showing. A craft-done for a DIFFERENT recipe
  // (a stale broadcast, or a second cook that replaced this one) must not hide
  // the bar that's mid-fill — guard finish() against it.
  let currentRecipeId: string | null = null;

  function hide(): void {
    root.style.display = "none";
    fillEl.style.transition = "none";
    fillEl.style.width = "0%";
    currentRecipeId = null;
    if (safetyTimer !== null) { clearTimeout(safetyTimer); safetyTimer = null; }
  }

  return {
    start(recipeId: string, totalMs: number): void {
      currentRecipeId = recipeId;
      labelEl.textContent = `Cooking ${recipeLabel(recipeId)}…`;
      root.style.display = "block";
      // Reset to empty with no transition, force a reflow, then animate to full
      // so the bar always starts from 0% even on a back-to-back cook.
      fillEl.style.transition = "none";
      fillEl.style.width = "0%";
      void fillEl.offsetWidth;
      fillEl.style.transition = `width ${totalMs}ms linear`;
      fillEl.style.width = "100%";
      if (safetyTimer !== null) clearTimeout(safetyTimer);
      safetyTimer = window.setTimeout(hide, totalMs + 2000);
    },
    finish(recipeId: string, success: boolean): void {
      // Only hide if this completion is for the cook we're actually showing.
      // A different recipe finishing (stale or superseded) leaves the bar alone.
      if (currentRecipeId === null || recipeId === currentRecipeId) hide();
      const label = recipeLabel(recipeId);
      notify?.(success ? `${label} is ready!` : `Couldn't finish ${label}`);
    },
  };
}
